import React from 'react';
import { find, isNil, isEqual } from 'lodash';

// React view component to render
import { SERVICE_RESPONSE_STATUS, CRUD_OPERATION } from 'constants/common';
import { offCallDispositionNotes as OffCallDispositionNotesView } from 'ui/views/widgets/offCallDispositionNotes';
import { OffCallDispositionNotesService } from 'services/offCallDispositionNotes';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as confirmOverlay from 'utils/confirmOverlay';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { OffCallDispositionNotes } from 'ui/containers/OffCallDispositionNotes';
 *
 * // render
 * <OffCallDispositionNotes propName='propValue'/>
 */
export class OffCallDispositionNotes extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind methods to instance
		this.__bind();

		// component state
		this.state = {
			isFetching: true,
			networkStatus: undefined,
			componentData: undefined,
			mode: 'readonly', // could be `readonly` or `editable`
			editItemId: null, // when an item is in edit mode
		};

		// create service instance
		this.offCallDispositionNotesService = new OffCallDispositionNotesService();

		// start component data fetch
        this.fetchComponentData( this.props.memberId );
        
    }

    // bind methods and events to instance
	__bind() {
		this.handleToggleEditMode = this.handleToggleEditMode.bind( this );
		this.handleFormSubmit = this.handleFormSubmit.bind( this );
        this.handleDeleteClick = this.handleDeleteClick.bind( this );
	}

	/*********************************************/
	
	UNSAFE_componentWillReceiveProps ( newProps ) {
		log.debug( 'OffCallDispositionNotes.componentWillReceiveProps()' );

		// if `locations` changes, re-render map
		if( ! isEqual( newProps.memberId, this.props.memberId ) ) {

			// start component data fetch
			this.fetchComponentData( newProps.memberId ); // re-render map if map dependencies are loaded
		}
	}
    
    /**
	 * @desc Fetch component response.
	 */
	async fetchComponentData( memberId ) {
		log.debug( 'OffCallDispositionNotes.fetchComponentData()' );

		// cancel ongoing request
		this.offCallDispositionNotesService.cancel();

		// get status and service data
		const { status, data } = await this.offCallDispositionNotesService.fetchData( {
			url: this.props.widget.url,
			data: {
				memberId: memberId,
				patientId: this.props.patientId!==undefined? this.props.patientId:''
			}
		} );

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
			} );
		} else {
			this.setState( {
				isFetching: false,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
				componentData: data,
			} );
		}
    }

    /**
	 * @desc Toggle between read and edit mode.
	 */
	handleToggleEditMode( editable = true, editItemId = null ) {
		log.debug( 'OffCallDispositionNotes.handleToggleEditMode()' );

		this.setState( {
			mode: editable ? 'editable' : 'readonly',
			editItemId, // when `edit` action is clicked
		} );
    }
    
    /**
	 * @desc Handle form submit.
	 */
	async handleFormSubmit( { id = null, title, description } = {} ) {
		log.debug( 'CallDispositionNotes.handleFormSubmit()' );

		// get save item action
		const saveAction = find( this.state.componentData.actions, { name: 'save' } );

		// cancel ongoing request
		this.offCallDispositionNotesService.cancel();

		// show page level preloader
		pageLevelPreloader.show();

		// get status and service data
		const {
			status
		} = await this.offCallDispositionNotesService.addUpdateItem( {
			url: saveAction.url,
			data: {
				operation: isNil( id ) ? CRUD_OPERATION.CREATE : CRUD_OPERATION.UPDATE,
				id,
				title,
				description,
				memberId: this.props.memberId,
				patientId: this.props.patientId!==undefined? this.props.patientId:''
			},
		} );

		// hide page level preloader
		pageLevelPreloader.hide();

		// check if response status was success
		if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
			this.setState( {
				isFetching: true,
				mode: 'readonly',
				editItemId: null,
				networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
			}, () =>  this.fetchComponentData( this.props.memberId ) );
		} else {
			errorOverlay.open(); // show error
		}
	}
    
    /**
	 * @desc Handle item `delete` action.
	 */
	async handleDeleteClick( { id } = {} ) {
		log.debug( 'CallDispositionNotes.handleDeleteClick()' );

		// get delete item action
		const deleteAction = find( this.state.componentData.actions, {
			name: 'delete',
		} );

		const shouldProceed = await confirmOverlay.open( {
			message: getLabel( deleteAction.confirmation ),
			iconName: 'close'
		} );

		if ( shouldProceed ) {

			// cancel ongoing request
			this.offCallDispositionNotesService.cancel();

			// show page level preloader
			pageLevelPreloader.show();

			// get status and service data
			const { status } = await this.offCallDispositionNotesService.deleteItem( {
				url: deleteAction.url,
				data: {
					id: id,
					memberId: this.props.memberId,
					patientId: this.props.patientId!==undefined? this.props.patientId:''
				},
			} );

			// hide page level preloader
			pageLevelPreloader.hide();

			// check if response status was success
			if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
				this.setState( {
					isFetching: true,
					networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
				}, () =>  this.fetchComponentData( this.props.memberId ) );
			} else {
				errorOverlay.open(); // show error
            }
        }
	}

	/*********************************************/

	/**
	 * @desc When component unmounts, cleanup resouces.
	 */
	componentWillUnmount() {
		this.offCallDispositionNotesService.cancel(); // cancel ongoing requests
    }
    
    /*********************************************/

    /**
     * @desc Render a React view component
     */
    render() {
		
        return (
            <OffCallDispositionNotesView
				isFetching={this.state.isFetching}
				networkStatus={this.state.networkStatus}
				mode={this.state.mode}
				editItemId={this.state.editItemId}
				{...this.state.componentData}
				onToggleEditMode={this.handleToggleEditMode}
				onFormSubmit={this.handleFormSubmit}
                onDeleteClick={this.handleDeleteClick}
			/>
        );
    }
}

// set display name
OffCallDispositionNotes.displayName = 'OffCallDispositionNotes';

// set default props
OffCallDispositionNotes.defaultProps = {};

// prop types
OffCallDispositionNotes.propTypes = {};
